import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Card } from "./ui/card";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Upload, Loader2, FileText, Image as ImageIcon, Download, X, Camera, Sparkles, Activity, MessageSquare, Eye, Code, Users, Mic, Video, TrendingUp, BarChart3, PieChart, Send, Globe, ChevronLeft, ChevronRight, Play, Cpu, Headphones, Database, Brain, Info, Zap, Square, RotateCw } from "lucide-react";
import { emotionExamples, mockAnalyzeText, mockAnalyzeImage } from "../mock";
import { toast } from "../hooks/use-toast";
import { useAnalytics } from "../contexts/AnalyticsContext";
import CameraCapture from "./CameraCapture";
import PanoramaViewer from "./PanoramaViewer";
import SectionHeader from "./SectionHeader";
import EmotionArc from "./EmotionArc";

const DemoSection = () => {
  const { trackAnalysis, trackMultipleEmotions, track3DVisualization, analytics, getEmotionDistribution, getTopEmotion, getPeakHour, getLastHourAnalyses } = useAnalytics();
  
  // Helper function to normalize emotion names for 3D cube and analytics
  const normalizeEmotionFor3DCube = (emotion) => {
    const emotionMap = {
      // Joy family
      'happy': 'joy',
      'happiness': 'joy',
      'joy': 'joy',
      'excitement': 'joy',
      'amazement': 'joy',
      
      // Sadness family
      'sad': 'sad',
      'sadness': 'sad',
      'sorrow': 'sad',
      'grief': 'sad',
      
      // Anger family
      'angry': 'anger',
      'anger': 'anger',
      'rage': 'anger',
      'frustration': 'anger',
      'frustrated': 'anger',
      
      // Fear family
      'fear': 'fear',
      'anxiety': 'fear',
      'worry': 'fear',
      
      // Surprise family
      'surprised': 'surprise',
      'surprise': 'surprise',
      'shock': 'surprise',
      
      // Disgust family
      'disgust': 'disgust',
      'contempt': 'disgust',
      
      // Neutral family
      'neutral': 'neutral',
      'calm': 'neutral',
      'peaceful': 'neutral',
      
      // Other emotions
      'love': 'joy',
      'affection': 'joy',
      'pride': 'joy',
      'confidence': 'joy',
      'shame': 'sad',
      'embarrassment': 'sad',
      'confusion': 'neutral',
      'bewilderment': 'surprise'
    };
    
    const key = emotion.toLowerCase().trim();
    return emotionMap[key] || 'neutral';
  };
  
  const [textInput, setTextInput] = useState("");
  const [activeTab, setActiveTab] = useState("text");
  const [textResult, setTextResult] = useState(null);
  const [isAnalyzingText, setIsAnalyzingText] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageResult, setImageResult] = useState(null);
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraMode, setCameraMode] = useState('single'); // 'single' or 'multi'
  const [chatMessages, setChatMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! I'm your AI Research Assistant powered by GPT-4o-mini. I can help you understand our emotion detection technology, research projects, and answer questions about facial emotion recognition. What would you like to know?"
    }
  ]);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [multiPersonImage, setMultiPersonImage] = useState(null);
  const [multiPersonPreview, setMultiPersonPreview] = useState(null);
  const [multiPersonResult, setMultiPersonResult] = useState(null);
  const [isAnalyzingMultiPerson, setIsAnalyzingMultiPerson] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [voiceResult, setVoiceResult] = useState(null);
  const [recognitionInstance, setRecognitionInstance] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [currentStation, setCurrentStation] = useState(0);
  const [isAutoTour, setIsAutoTour] = useState(false);
  
  // Sandbox collaboration states
  const [sandboxText, setSandboxText] = useState("");
  const [sandboxAnalyzing, setSandboxAnalyzing] = useState(false);
  const [sharedAnalyses, setSharedAnalyses] = useState([]);
  const [collabChatMessage, setCollabChatMessage] = useState("");
  const [collabChatMessages, setCollabChatMessages] = useState([]);
  const [loadingShared, setLoadingShared] = useState(false);
  const [userName, setUserName] = useState("");
  const [showNameDialog, setShowNameDialog] = useState(false);
  
  // Video timeline analysis states
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [isRecordingVideo, setIsRecordingVideo] = useState(false);
  const [isAnalyzingVideo, setIsAnalyzingVideo] = useState(false);
  const [videoTimeline, setVideoTimeline] = useState([]);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [currentEmotion, setCurrentEmotion] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [mediaRecorderVideo, setMediaRecorderVideo] = useState(null);
  const [videoStream, setVideoStream] = useState(null);
  const videoRecordRef = useRef(null);
  const videoPreviewRef = useRef(null);
  
  // Generate a user ID for this session
  const [userId] = useState(() => {
    const stored = localStorage.getItem('emotion_ai_user_id');
    if (stored) return stored;
    const newId = `user_${Math.random().toString(36).substring(7)}`;
    localStorage.setItem('emotion_ai_user_id', newId);
    return newId;
  });
  
  // Initialize username from localStorage
  useEffect(() => {
    const storedName = localStorage.getItem('emotion_ai_username');
    if (storedName) {
      setUserName(storedName);
    }
  }, []);
  
  // Function to ensure user has a username before sandbox interactions
  const ensureUsername = () => {
    if (!userName || userName.trim() === '') {
      const name = prompt('Please enter your name to participate in the sandbox:');
      if (name && name.trim() !== '') {
        const trimmedName = name.trim();
        setUserName(trimmedName);
        localStorage.setItem('emotion_ai_username', trimmedName);
        return trimmedName;
      }
      return null;
    }
    return userName;
  };
  
  // Load shared analyses and chat messages on mount
  useEffect(() => {
    loadSharedData();
    // Poll for updates every 10 seconds
    const interval = setInterval(loadSharedData, 10000);
    return () => clearInterval(interval);
  }, []);
  
  // Preload lab tour images for better performance
  useEffect(() => {
    const imagesToPreload = [
      '/emotion-analysis-lab.jpg',
      '/neural-recording-lab.jpg',
      '/multi-modal-lab.jpg',
      '/validation-testing-lab.png'
    ];
    
    imagesToPreload.forEach(src => {
      const img = new Image();
      img.src = src;
    });
    
    console.log('🖼️ Preloading lab tour images for caching');
  }, []);
  
  // Auto Tour functionality - cycle through stations every 8 seconds
  useEffect(() => {
    if (!isAutoTour) return;
    
    const interval = setInterval(() => {
      setCurrentStation(prev => {
        // If we're at the last station, go back to first
        if (prev >= labStations.length - 1) {
          return 0;
        }
        // Otherwise, go to next station
        return prev + 1;
      });
    }, 8000); // Change station every 8 seconds
    
    return () => clearInterval(interval);
  }, [isAutoTour]);

  const labStations = [
    {
      id: 1,
      title: "Emotion Analysis Workstation",
      description: "High-performance computing cluster for real-time emotion processing",
      image: "/emotion-analysis-lab.jpg",
      is360: true,
      equipment: [
        {
          icon: Cpu,
          color: "green",
          name: "NVIDIA A100 GPU Cluster",
          desc: "8x NVIDIA A100 GPUs for deep learning inference with 40GB HBM2 memory each"
        },
        {
          icon: Camera,
          color: "blue",
          name: "High-Resolution Cameras",
          desc: "4K cameras with advanced facial recognition sensors for emotion capture"
        },
        {
          icon: Headphones,
          color: "purple",
          name: "Audio Processing Unit",
          desc: "Professional-grade microphone arrays for voice emotion analysis"
        }
      ]
    },
    {
      id: 2,
      title: "Neural Recording Laboratory",
      description: "Advanced fNIRS and biometric sensors for emotion detection",
      image: "/neural-recording-lab.jpg",
      is360: true,
      equipment: [
        {
          icon: Brain,
          color: "pink",
          name: "fNIRS Imaging System",
          desc: "Functional near-infrared spectroscopy for brain activity monitoring"
        },
        {
          icon: Activity,
          color: "red",
          name: "Biometric Sensors",
          desc: "Heart rate, skin conductance, and temperature monitoring systems"
        },
        {
          icon: Database,
          color: "green",
          name: "Real-time Data Pipeline",
          desc: "Low-latency data acquisition and processing infrastructure"
        }
      ]
    },
    {
      id: 3,
      title: "Multi-Modal Integration Lab",
      description: "Fusion of visual, audio, and physiological signals",
      image: "/multi-modal-lab.jpg",
      is360: true,
      equipment: [
        {
          icon: Video,
          color: "purple",
          name: "Synchronized Recording",
          desc: "Multi-camera setups with precise temporal alignment"
        },
        {
          icon: Mic,
          color: "blue",
          name: "Spatial Audio Array",
          desc: "Beamforming microphone arrays for voice isolation"
        },
        {
          icon: Cpu,
          color: "orange",
          name: "Edge AI Processors",
          desc: "Dedicated hardware for real-time multi-modal fusion"
        }
      ]
    },
    {
      id: 4,
      title: "Validation & Testing Suite",
      description: "Controlled environment for model evaluation and benchmarking",
      image: "/validation-testing-lab.png",
      is360: false,
      equipment: [
        {
          icon: BarChart3,
          color: "green",
          name: "Performance Metrics",
          desc: "Comprehensive accuracy and latency testing infrastructure"
        },
        {
          icon: Users,
          color: "blue",
          name: "Human Subject Studies",
          desc: "IRB-approved participant testing facilities"
        },
        {
          icon: Database,
          color: "purple",
          name: "Benchmark Datasets",
          desc: "Curated emotion datasets for model validation"
        }
      ]
    }
  ];

  // Load shared data from backend
  const loadSharedData = async () => {
    if (loadingShared) return;
    
    setLoadingShared(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Load shared analyses
      const analysesResponse = await axios.get(`${BACKEND_URL}/api/shared-analyses?limit=50`);
      if (analysesResponse.data.success) {
        const formattedAnalyses = analysesResponse.data.analyses.map(a => ({
          id: a.id,
          user: a.user_id === userId ? 'You' : (a.username || `User ${a.user_id.substring(0, 8)}`),
          avatar: a.user_id === userId ? 'ME' : (a.username ? a.username.substring(0, 2).toUpperCase() : a.user_id.substring(5, 7).toUpperCase()),
          text: a.text.substring(0, 150) + (a.text.length > 150 ? "..." : ""),
          emotion: a.emotion,
          timestamp: new Date(a.timestamp),
          likes: a.likes,
          comments: a.comments
        }));
        setSharedAnalyses(formattedAnalyses);
      }
      
      // Load chat messages
      const chatResponse = await axios.get(`${BACKEND_URL}/api/sandbox-chat?limit=100`);
      if (chatResponse.data.success) {
        const formattedMessages = chatResponse.data.messages.map(m => ({
          id: m.id,
          user: m.user_id === userId ? 'You' : (m.username || `User ${m.user_id.substring(0, 8)}`),
          avatar: m.user_id === userId ? 'ME' : (m.username ? m.username.substring(0, 2).toUpperCase() : m.user_id.substring(5, 7).toUpperCase()),
          message: m.message,
          timestamp: new Date(m.timestamp)
        }));
        setCollabChatMessages(formattedMessages);
      }
    } catch (error) {
      console.error('Error loading shared data:', error);
    } finally {
      setLoadingShared(false);
    }
  };

  const handleTextAnalysis = async () => {
    if (!textInput.trim()) {
      toast({
        title: "Input Required",
        description: "Please enter some text to analyze",
        variant: "destructive"
      });
      return;
    }

    setIsAnalyzingText(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/analyze-text-emotion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: textInput })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setTextResult({ primaryEmotion: result.emotion });
        
        console.log('📊 Text analysis result:', result.emotion);
        
        // Track analytics
        console.log('📊 Calling trackAnalysis for text...');
        trackAnalysis('text', result.emotion, 95);
        console.log('✅ Analytics tracked');
        
        // Update 3D Emotion Cube with normalized emotion name
        if (window.updateEmotionCube) {
          const normalizedEmotion = normalizeEmotionFor3DCube(result.emotion);
          console.log(`🎲 Updating 3D cube: ${result.emotion} → ${normalizedEmotion}`);
          window.updateEmotionCube([{ emotion: normalizedEmotion, confidence: 95 }]);
        } else {
          console.warn('⚠️ window.updateEmotionCube not available');
        }
        
        toast({
          title: "Analysis Complete",
          description: `Detected emotion: ${result.emotion}`
        });
      } else {
        throw new Error('Analysis failed');
      }
    } catch (error) {
      console.error('Error analyzing text:', error);
      toast({
        title: "Analysis Failed",
        description: "Failed to analyze text. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzingText(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setImageResult(null);
    }
  };

  const handleImageAnalysis = async () => {
    if (!imageFile) {
      toast({
        title: "Image Required",
        description: "Please upload an image to analyze",
        variant: "destructive"
      });
      return;
    }

    setIsAnalyzingImage(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      formData.append('file', imageFile);
      
      const response = await fetch(`${BACKEND_URL}/api/analyze-emotion`, {
        method: 'POST',
        body: formData
      });
      
      const result = await response.json();
      
      if (!result.face_detected) {
        setImageResult({ faceDetected: false, error: result.message });
        toast({
          title: "No Face Detected",
          description: result.message || "Please upload an image with a clear face",
          variant: "destructive"
        });
      } else {
        setImageResult({
          faceDetected: true,
          detectedEmotion: result.emotion,
          confidence: result.confidence,
          emotionDistribution: result.emotion_distribution,
          imageWithBox: result.image_with_box
        });
        
        // Track analytics
        trackAnalysis('image', result.emotion, result.confidence);
        
        // Update 3D Emotion Cube with normalized emotion name
        if (window.updateEmotionCube) {
          const normalizedEmotion = normalizeEmotionFor3DCube(result.emotion);
          window.updateEmotionCube([{ emotion: normalizedEmotion, confidence: result.confidence }]);
        }
        
        toast({
          title: "Analysis Complete",
          description: `Detected emotion: ${result.emotion}`
        });
      }
    } catch (error) {
      console.error('Error analyzing image:', error);
      toast({
        title: "Analysis Failed",
        description: "Failed to analyze image. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzingImage(false);
    }
  };

  const handleExampleClick = (example) => {
    setTextInput(example.text);
    setTextResult(null);
  };

  const handleDownloadResult = () => {
    if (!imageResult || !imageResult.imageWithBox) return;
    
    try {
      // Create canvas to draw annotated image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Create image from base64
      const img = new Image();
      img.onload = () => {
        // Set canvas size (add space for emotion data)
        const padding = 280;
        canvas.width = img.width + padding;
        canvas.height = img.height;
        
        // Fill background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Draw the image with bounding box
        ctx.drawImage(img, 0, 0);
        
        // Draw emotion data panel on the right
        const panelX = img.width + 20;
        const startY = Math.floor(img.height / 2) - 60; // Center vertically
        
        // Title
        ctx.fillStyle = '#6b7280';
        ctx.font = '20px Arial';
        ctx.fillText('DETECTED EMOTION', panelX, startY);
        
        // Primary emotion - Large and centered
        ctx.fillStyle = '#00BCD4';
        ctx.font = 'bold 48px Arial';
        ctx.fillText(imageResult.detectedEmotion, panelX, startY + 70);
        
        // Footer text
        ctx.fillStyle = '#9ca3af';
        ctx.font = '14px Arial';
        ctx.fillText('Keimyung AI Research Center', panelX, canvas.height - 30);
        ctx.font = '12px Arial';
        ctx.fillText('Facial Emotion Recognition', panelX, canvas.height - 10);
        
        // Convert canvas to blob and download
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
          const emotion = imageResult.detectedEmotion.toLowerCase();
          const confidence = imageResult.confidence.toFixed(0);
          link.download = `emotion-${emotion}-${confidence}pct-${timestamp}.jpg`;
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          toast({
            title: "Downloaded Successfully!",
            description: `Annotated image saved to Downloads folder`
          });
        }, 'image/jpeg', 0.95);
      };
      
      img.src = `data:image/jpeg;base64,${imageResult.imageWithBox}`;
      
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download Failed",
        description: "Unable to download the image. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleCancelImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageResult(null);
    // Reset file input
    const fileInput = document.getElementById('image-upload');
    if (fileInput) fileInput.value = '';
  };

  const handleCameraCapture = (file) => {
    if (cameraMode === 'multi') {
      // Multi-person detection
      setMultiPersonImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setMultiPersonPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setMultiPersonResult(null);
    } else {
      // Single image detection
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setImageResult(null);
    }
    setShowCamera(false);
  };

  // Handle sandbox text analysis and sharing
  const handleSandboxShare = async () => {
    if (!sandboxText.trim() || sandboxAnalyzing) return;
    
    // Ensure user has a username
    const currentUserName = ensureUsername();
    if (!currentUserName) {
      toast({
        title: "Username Required",
        description: "You need to enter a name to share analyses.",
        variant: "destructive"
      });
      return;
    }
    
    setSandboxAnalyzing(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Analyze the text
      const analysisResponse = await axios.post(`${BACKEND_URL}/api/analyze-text-emotion`, {
        text: sandboxText
      });
      
      if (analysisResponse.data.success) {
        // Save to shared analyses
        const shareResponse = await axios.post(`${BACKEND_URL}/api/share-analysis`, {
          user_id: userId,
          username: currentUserName,
          text: sandboxText,
          emotion: analysisResponse.data.emotion
        });
        
        if (shareResponse.data.success) {
          // Track in analytics
          trackAnalysis('text', analysisResponse.data.emotion, 95);
          
          // Clear input
          setSandboxText("");
          
          // Reload shared data to show the new analysis
          await loadSharedData();
          
          toast({
            title: "Analysis Shared!",
            description: `Detected ${analysisResponse.data.emotion} - Your analysis is now visible to collaborators`
          });
        } else {
          throw new Error('Failed to share analysis');
        }
      }
    } catch (error) {
      console.error('Sandbox share error:', error);
      toast({
        title: "Analysis Failed",
        description: "Unable to analyze and share. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSandboxAnalyzing(false);
    }
  };
  
  // Video Timeline Analysis Handlers
  const extractVideoFrame = (videoElement, time) => {
    return new Promise((resolve) => {
      videoElement.currentTime = time;
      videoElement.onseeked = () => {
        const canvas = document.createElement('canvas');
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 0.8);
      };
    });
  };

  const analyzeVideoFrames = async (videoSrc) => {
    setIsAnalyzingVideo(true);
    setVideoTimeline([]);
    
    try {
      const videoElement = document.createElement('video');
      videoElement.src = videoSrc;
      videoElement.crossOrigin = 'anonymous';
      
      await new Promise((resolve) => {
        videoElement.onloadedmetadata = resolve;
      });
      
      const duration = videoElement.duration;
      const frameInterval = 2; // Analyze every 2 seconds
      const timeline = [];
      
      toast({
        title: "Analyzing Video",
        description: `Processing ${Math.ceil(duration / frameInterval)} frames...`
      });
      
      for (let time = 0; time < duration; time += frameInterval) {
        const frameBlob = await extractVideoFrame(videoElement, time);
        const formData = new FormData();
        formData.append('file', frameBlob, `frame-${time}s.jpg`);
        
        try {
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
          const response = await axios.post(`${BACKEND_URL}/api/analyze-emotion`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          if (response.data.success) {
            timeline.push({
              time: time,
              emotion: response.data.emotion,
              confidence: response.data.confidence || 85
            });
            
            setVideoTimeline([...timeline]);
          }
        } catch (error) {
          console.error(`Error analyzing frame at ${time}s:`, error);
        }
      }
      
      toast({
        title: "Analysis Complete",
        description: `Analyzed ${timeline.length} frames successfully`
      });
      
    } catch (error) {
      console.error('Video analysis error:', error);
      toast({
        title: "Analysis Failed",
        description: "Failed to analyze video. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzingVideo(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setVideoFile(file);
    const videoUrl = URL.createObjectURL(file);
    setVideoPreview(videoUrl);
    setVideoTimeline([]);
    
    // Auto-analyze
    await analyzeVideoFrames(videoUrl);
  };

  const startVideoRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' }, 
        audio: false 
      });
      
      setVideoStream(stream);
      
      if (videoRecordRef.current) {
        videoRecordRef.current.srcObject = stream;
      }
      
      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      const chunks = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        const videoUrl = URL.createObjectURL(blob);
        setVideoPreview(videoUrl);
        setVideoFile(new File([blob], 'recording.webm', { type: 'video/webm' }));
        
        // Stop camera
        stream.getTracks().forEach(track => track.stop());
        setVideoStream(null);
        
        // Analyze recorded video
        await analyzeVideoFrames(videoUrl);
      };
      
      recorder.start();
      setMediaRecorderVideo(recorder);
      setIsRecordingVideo(true);
      setRecordingDuration(0);
      setVideoTimeline([]);
      
      // Real-time emotion detection every 3 seconds
      const realtimeInterval = setInterval(async () => {
        if (!videoRecordRef.current) return;
        
        const canvas = document.createElement('canvas');
        const video = videoRecordRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        
        canvas.toBlob(async (blob) => {
          try {
            const formData = new FormData();
            formData.append('file', blob, 'frame.jpg');
            
            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.post(`${BACKEND_URL}/api/analyze-emotion`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (response.data.success) {
              setCurrentEmotion({
                emotion: response.data.emotion,
                confidence: response.data.confidence || 85
              });
            }
          } catch (error) {
            console.error('Real-time analysis error:', error);
          }
        }, 'image/jpeg', 0.8);
        
      }, 3000);
      
      // Duration tracker
      const durationInterval = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      
      // Store intervals for cleanup
      recorder.realtimeInterval = realtimeInterval;
      recorder.durationInterval = durationInterval;
      
    } catch (error) {
      console.error('Video recording error:', error);
      toast({
        title: "Recording Failed",
        description: "Failed to access camera. Please check permissions.",
        variant: "destructive"
      });
    }
  };

  const stopVideoRecording = () => {
    if (mediaRecorderVideo && mediaRecorderVideo.state !== 'inactive') {
      clearInterval(mediaRecorderVideo.realtimeInterval);
      clearInterval(mediaRecorderVideo.durationInterval);
      mediaRecorderVideo.stop();
      setIsRecordingVideo(false);
      setCurrentEmotion(null);
    }
  };

  // Handle chat message sending
  const handleSendChatMessage = async () => {
    if (!collabChatMessage.trim()) return;
    
    // Ensure user has a username
    const currentUserName = ensureUsername();
    if (!currentUserName) {
      toast({
        title: "Username Required",
        description: "You need to enter a name to send messages.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await axios.post(`${BACKEND_URL}/api/sandbox-chat`, {
        user_id: userId,
        username: currentUserName,
        message: collabChatMessage
      });
      
      if (response.data.success) {
        setCollabChatMessage("");
        
        // Reload chat messages to show the new message
        await loadSharedData();
        
        toast({
          title: "Message Sent",
          description: "Your message has been shared with collaborators"
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Chat message error:', error);
      toast({
        title: "Send Failed",
        description: "Unable to send message. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleChatQuestion = async (question) => {
    if (isChatLoading) return;
    
    // Add user message immediately
    setChatMessages(prev => [...prev, { role: 'user', content: question }]);
    setIsChatLoading(true);
    
    // Add a "thinking" message
    const thinkingMsg = { role: 'assistant', content: 'Thinking...' };
    setChatMessages(prev => [...prev, thinkingMsg]);
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        message: question,
        conversation_history: chatMessages
      });
      
      if (response.data.success) {
        // Replace thinking message with actual response
        setChatMessages(prev => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = {
            role: 'assistant',
            content: response.data.response
          };
          return newMessages;
        });
      } else {
        throw new Error('Chat failed');
      }
    } catch (error) {
      console.error('Chat error:', error);
      // Replace thinking message with error
      setChatMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = {
          role: 'assistant',
          content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment."
        };
        return newMessages;
      });
      
      toast({
        title: "Chat Error",
        description: "Unable to get response. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleMultiPersonUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setMultiPersonImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setMultiPersonPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setMultiPersonResult(null);
    }
  };

  const handleMultiPersonAnalysis = async () => {
    if (!multiPersonImage) {
      toast({
        title: "Image Required",
        description: "Please upload an image to analyze",
        variant: "destructive"
      });
      return;
    }

    setIsAnalyzingMultiPerson(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      formData.append('file', multiPersonImage);
      
      const response = await fetch(`${BACKEND_URL}/api/analyze-multi-person`, {
        method: 'POST',
        body: formData
      });
      
      const result = await response.json();
      
      if (result.success) {
        setMultiPersonResult(result);
        
        // Track analytics for multiple emotions
        const emotionsData = result.emotions.map(person => ({
          emotion: person.emotion,
          confidence: person.confidence
        }));
        trackMultipleEmotions(emotionsData);
        
        // Update 3D Emotion Cube with all detected emotions (normalized)
        if (window.updateEmotionCube) {
          const normalizedEmotions = emotionsData.map(e => ({
            emotion: normalizeEmotionFor3DCube(e.emotion),
            confidence: e.confidence
          }));
          window.updateEmotionCube(normalizedEmotions);
        }
        
        toast({
          title: "Analysis Complete",
          description: `Detected ${result.people_count} ${result.people_count === 1 ? 'person' : 'people'}`
        });
      } else {
        throw new Error('Analysis failed');
      }
    } catch (error) {
      console.error('Error analyzing image:', error);
      toast({
        title: "Analysis Failed",
        description: "Failed to analyze image. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzingMultiPerson(false);
    }
  };

  const handleVoiceRecording = async () => {
    // Check browser support
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast({
        title: "Not Supported",
        description: "Speech recognition is not supported in your browser. Please use Chrome or Edge.",
        variant: "destructive"
      });
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast({
        title: "Not Supported",
        description: "Audio recording is not supported in your browser.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Reset states
      setVoiceTranscript("");
      setVoiceResult(null);
      setAudioChunks([]);

      console.log('🚀 Starting advanced voice emotion analysis...');

      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true
        } 
      });
      
      // Setup audio recording with WAV format
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/wav')) {
        mimeType = 'audio/wav';
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=pcm')) {
        mimeType = 'audio/webm;codecs=pcm';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      }
      
      console.log('Using MIME type:', mimeType);
      
      const recorder = new MediaRecorder(stream, { 
        mimeType: mimeType,
        audioBitsPerSecond: 128000
      });
      const chunks = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
          console.log('📼 Audio chunk recorded:', e.data.size, 'bytes');
        }
      };
      
      recorder.onstop = async () => {
        console.log('🛑 Audio recording stopped, processing...');
        
        // Create audio blob
        const audioBlob = new Blob(chunks, { type: 'audio/wav' });
        console.log('🎵 Audio blob created:', audioBlob.size, 'bytes');
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      // Setup speech recognition
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.lang = 'en-US';
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      let finalTranscript = '';
      let isCurrentlyRecording = true;

      recognition.onresult = (event) => {
        console.log('🎤 Speech recognition result');
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
            console.log('✅ Final transcript updated:', finalTranscript);
          } else {
            interimTranscript += transcript;
          }
        }
        
        setVoiceTranscript(finalTranscript + interimTranscript);
      };
      
      recognition.onstart = () => {
        console.log('🎤 Speech recognition started');
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'no-speech') return;
        
        isCurrentlyRecording = false;
        setIsRecording(false);
        setRecognitionInstance(null);
        recorder.stop();
      };

      recognition.onend = async () => {
        console.log('🛑 Speech recognition ended');
        console.log('Final transcript:', finalTranscript);
        console.log('Was recording:', isCurrentlyRecording);
        
        if (!isCurrentlyRecording) {
          console.log('ℹ️ Not processing - recording was stopped');
          return;
        }
        
        isCurrentlyRecording = false;
        setIsRecording(false);
        setRecognitionInstance(null);
        
        // Process audio and text
        if (finalTranscript.trim() && chunks.length > 0) {
          console.log('✅ Processing voice emotion (text + audio)...');
          
          try {
            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
            
            // Create audio blob
            const audioBlob = new Blob(chunks, { type: 'audio/wav' });
            
            // Analyze text emotion
            console.log('📝 Analyzing text emotion...');
            const textResponse = await axios.post(`${BACKEND_URL}/api/analyze-text-emotion`, {
              text: finalTranscript.trim()
            });
            
            const textEmotion = textResponse.data.success ? textResponse.data.emotion : 'Neutral';
            console.log('✅ Text emotion:', textEmotion);
            
            // Analyze voice features (pitch, tone, energy)
            console.log('🎵 Analyzing voice features...');
            const formData = new FormData();
            formData.append('file', audioBlob, 'recording.wav');
            
            const voiceResponse = await axios.post(`${BACKEND_URL}/api/analyze-voice-emotion`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (voiceResponse.data.success) {
              const voiceEmotion = voiceResponse.data.emotion;
              const voiceConfidence = voiceResponse.data.confidence;
              console.log('✅ Voice emotion:', voiceEmotion, 'Confidence:', voiceConfidence);
              
              // Combine results with weighted average
              // Text: 60%, Voice tone: 40%
              let finalEmotion = textEmotion;
              let analysis = {
                textEmotion: textEmotion,
                voiceEmotion: voiceEmotion,
                voiceConfidence: voiceConfidence
              };
              
              // If voice and text emotions differ significantly, show both
              if (textEmotion !== voiceEmotion && voiceConfidence > 60) {
                finalEmotion = `${textEmotion} (words) / ${voiceEmotion} (tone)`;
                analysis.hybrid = true;
              }
              
              setVoiceResult({ 
                emotion: finalEmotion,
                transcript: finalTranscript.trim(),
                textEmotion: textEmotion,
                voiceEmotion: voiceEmotion,
                voiceConfidence: voiceConfidence,
                hybrid: textEmotion !== voiceEmotion && voiceConfidence > 60
              });
              
              // Track analytics
              trackAnalysis('voice', finalEmotion, 90);
              
              // Update 3D Emotion Cube
              if (window.updateEmotionCube) {
                const normalizedEmotion = normalizeEmotionFor3DCube(textEmotion);
                window.updateEmotionCube([{ emotion: normalizedEmotion, confidence: 90 }]);
              }
              
              toast({
                title: "Voice Analysis Complete",
                description: `Text: ${textEmotion}, Voice: ${voiceEmotion}`
              });
            } else {
              // Fallback to text-only analysis
              setVoiceResult({ 
                emotion: textEmotion,
                transcript: finalTranscript.trim(),
                textEmotion: textEmotion
              });
              
              // Track analytics for text-only voice analysis
              trackAnalysis('voice', textEmotion, 85);
              
              // Update 3D Emotion Cube
              if (window.updateEmotionCube) {
                const normalizedEmotion = normalizeEmotionFor3DCube(textEmotion);
                window.updateEmotionCube([{ emotion: normalizedEmotion, confidence: 85 }]);
              }
              
              toast({
                title: "Voice Analysis Complete",
                description: `Detected emotion: ${textEmotion} (text-based)`
              });
            }
          } catch (error) {
            console.error('❌ Error analyzing voice:', error);
            toast({
              title: "Analysis Failed",
              description: "Failed to analyze voice. Please try again.",
              variant: "destructive"
            });
          }
        } else {
          console.warn('⚠️ No speech or audio detected');
          toast({
            title: "No Speech Detected",
            description: "Couldn't detect any speech. Please speak louder and try again.",
            variant: "destructive"
          });
        }
      };

      // Store references
      setRecognitionInstance(recognition);
      setMediaRecorder(recorder);
      
      // Start recording
      console.log('🎙️ Starting audio recording...');
      recorder.start();
      
      console.log('🎤 Starting speech recognition...');
      recognition.start();
      
      setIsRecording(true);
      
      toast({
        title: "🎤 Recording...",
        description: "Speak clearly. We're analyzing your words AND voice tone!"
      });

    } catch (error) {
      setIsRecording(false);
      console.error('❌ Voice recording error:', error);
      toast({
        title: "Recording Error",
        description: "Failed to access microphone. Please check permissions.",
        variant: "destructive"
      });
    }
  };
  
  const handleStopRecording = () => {
    console.log('🛑 Stop button clicked');
    
    // Stop audio recording
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      console.log('Stopping audio recorder...');
      mediaRecorder.stop();
    }
    
    // Stop speech recognition
    if (recognitionInstance) {
      console.log('Stopping speech recognition...');
      recognitionInstance.stop();
    }
    
    toast({
      title: "Processing...",
      description: "Analyzing your voice and tone..."
    });
  };

  return (
    <section id="demo" className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#FBF6E0]">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          eyebrow="Live Demo"
          title="AI Emotion Analysis"
          titleAccent="Across Every Modality"
          subtitle="Experience our cutting-edge emotion detection technology through text, image, voice, and multi-person facial recognition — all running in real time."
          index={4}
          className="mb-12 sm:mb-16"
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="mb-8 sm:mb-12 flex justify-center">
            <div className="inline-flex items-stretch bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <TabsList className="inline-flex bg-transparent p-0 h-auto border-0 rounded-none gap-0">
                <TabsTrigger value="text" className="flex items-center gap-1.5 text-xs py-2.5 px-4 text-gray-600 data-[state=active]:bg-gray-900 data-[state=active]:text-white data-[state=active]:shadow-none rounded-none transition-colors whitespace-nowrap">
                  <FileText className="w-3.5 h-3.5" />
                  <span>Text</span>
                </TabsTrigger>
                <TabsTrigger value="image" className="flex items-center gap-1.5 text-xs py-2.5 px-4 text-gray-600 data-[state=active]:bg-gray-900 data-[state=active]:text-white data-[state=active]:shadow-none rounded-none transition-colors whitespace-nowrap">
                  <ImageIcon className="w-3.5 h-3.5" />
                  <span>Image</span>
                </TabsTrigger>
                <TabsTrigger value="advanced" className="flex items-center gap-1.5 text-xs py-2.5 px-4 text-gray-600 data-[state=active]:bg-gray-900 data-[state=active]:text-white data-[state=active]:shadow-none rounded-none transition-colors whitespace-nowrap">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Advanced</span>
                </TabsTrigger>
                <TabsTrigger value="dashboard" className="flex items-center gap-1.5 text-xs py-2.5 px-4 text-gray-600 data-[state=active]:bg-gray-900 data-[state=active]:text-white data-[state=active]:shadow-none rounded-none transition-colors whitespace-nowrap">
                  <Activity className="w-3.5 h-3.5" />
                  <span>Dashboard</span>
                </TabsTrigger>
                <TabsTrigger value="chat" data-testid="demo-tab-chat" className="flex items-center gap-1.5 text-xs py-2.5 px-4 text-gray-600 data-[state=active]:bg-gray-900 data-[state=active]:text-white data-[state=active]:shadow-none rounded-none transition-colors whitespace-nowrap">
                  <MessageSquare className="w-3.5 h-3.5" />
                  <span>AI Chat</span>
                </TabsTrigger>
                <TabsTrigger value="lab" data-testid="demo-tab-lab" className="flex items-center gap-1.5 text-xs py-2.5 px-4 text-gray-600 data-[state=active]:bg-gray-900 data-[state=active]:text-white data-[state=active]:shadow-none rounded-none transition-colors whitespace-nowrap">
                  <Eye className="w-3.5 h-3.5" />
                  <span>Lab Tour</span>
                </TabsTrigger>
                <TabsTrigger value="sandbox" data-testid="demo-tab-sandbox" className="flex items-center gap-1.5 text-xs py-2.5 px-4 text-gray-600 data-[state=active]:bg-gray-900 data-[state=active]:text-white data-[state=active]:shadow-none rounded-none transition-colors whitespace-nowrap">
                  <Code className="w-3.5 h-3.5" />
                  <span>Sandbox</span>
                </TabsTrigger>
              </TabsList>
            </div>
          </div>

          <TabsContent value="text" className="space-y-8 px-4">
            <div className="text-center mb-8">
              <h3 className="text-2xl sm:text-3xl font-light text-gray-900 mb-3">
                Try Our Emotion Analysis
              </h3>
              <p className="text-sm sm:text-base text-gray-600 font-light max-w-2xl mx-auto px-4">
                Enter any text and see how our AI detects emotional content in real-time
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <Card className="p-4 sm:p-6 md:p-8 bg-gradient-to-br from-blue-50 to-purple-50 border-gray-200 shadow-sm">

                <div className="space-y-6">
                  <div>
                    <Label htmlFor="text-input" className="text-base font-medium text-gray-900 mb-3 block">
                      Text Input
                    </Label>
                    <Textarea
                      id="text-input"
                      placeholder="Enter your text here... (e.g., 'I'm so excited about this new project! It makes me feel confident and happy :)')"
                      value={textInput}
                      onChange={(e) => setTextInput(e.target.value)}
                      className="min-h-[150px] resize-none bg-white"
                    />
                    <p className="text-sm text-gray-500 mt-2">{textInput.length}/5000 characters</p>
                  </div>

                  <Button
                    onClick={handleTextAnalysis}
                    disabled={isAnalyzingText}
                    className="bg-gray-800 hover:bg-gray-900 text-white px-8 py-3"
                  >
                    {isAnalyzingText ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      "ANALYZE EMOTIONS"
                    )}
                  </Button>
                </div>
              </Card>
            </div>

            {textResult && (
              <div className="max-w-3xl mx-auto mt-8 px-4">
                <Card className="p-6 sm:p-10 bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 shadow-md">
                  <div className="text-center">
                    <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider mb-4 sm:mb-6">Detected Emotion</p>
                    <p className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#00BCD4] to-[#0097A7] bg-clip-text text-transparent pb-2 break-words">
                      {textResult.primaryEmotion}
                    </p>
                  </div>
                </Card>
              </div>
            )}

            <div className="max-w-3xl mx-auto mt-8">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-lg border border-amber-200">
              <h4 className="text-xl font-light text-gray-900 mb-6">Try These Examples:</h4>
              <div className="space-y-3">
                {emotionExamples.map((example) => (
                  <button
                    key={example.id}
                    onClick={() => handleExampleClick(example)}
                    className="w-full text-left p-4 bg-white rounded-md border border-gray-200 hover:border-[#00BCD4] hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start gap-3">
                      <span className="inline-block px-2 py-1 bg-gray-100 text-xs text-gray-700 rounded group-hover:bg-cyan-100 group-hover:text-[#00BCD4] transition-colors">
                        {example.emotion}
                      </span>
                      <p className="flex-1 text-sm text-gray-700 font-light">{example.text}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            </div>
          </TabsContent>

          <TabsContent value="image" className="space-y-8 px-4">
            <div className="text-center mb-8">
              <h3 className="text-2xl sm:text-3xl font-light text-gray-900 mb-3">
                Image Emotion Analysis
              </h3>
              <p className="text-sm sm:text-base text-gray-600 font-light max-w-2xl mx-auto px-4">
                Upload a photo or use your camera to analyze facial emotions in real-time
              </p>
            </div>

            <div className="max-w-3xl mx-auto px-4">
              <Card className="p-4 sm:p-6 md:p-8 bg-gradient-to-br from-blue-50 to-purple-50 border-gray-200 shadow-sm">
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <Label className="text-sm sm:text-base font-medium text-gray-900 mb-3 sm:mb-4 block">
                      Image Input
                    </Label>
                    
                    {imagePreview ? (
                      <div className="mb-6">
                        <div className="border-2 border-gray-300 rounded-lg p-4 bg-white">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="max-w-full mx-auto rounded-lg shadow-md"
                          />
                        </div>
                        <div className="flex justify-center gap-4 mt-4">
                          <Button
                            onClick={handleImageAnalysis}
                            disabled={isAnalyzingImage}
                            className="bg-gray-800 hover:bg-gray-900 text-white px-8 py-3"
                          >
                            {isAnalyzingImage ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Analyzing...
                              </>
                            ) : (
                              "ANALYZE EMOTIONS"
                            )}
                          </Button>
                          <Button
                            onClick={handleCancelImage}
                            variant="outline"
                            className="px-8 py-3"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                        <Button
                          onClick={() => {
                            setCameraMode('single');
                            setShowCamera(true);
                          }}
                          variant="outline"
                          className="flex items-center justify-center gap-2 px-4 sm:px-6 py-4 sm:py-6 text-sm sm:text-base border-2 hover:border-gray-400 transition-colors w-full sm:w-auto"
                        >
                          <Camera className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span>START CAMERA</span>
                        </Button>
                        
                        <Button
                          variant="outline"
                          className="flex items-center justify-center gap-2 px-4 sm:px-6 py-4 sm:py-6 text-sm sm:text-base border-2 hover:border-gray-400 transition-colors w-full sm:w-auto"
                          onClick={() => document.getElementById('image-upload').click()}
                        >
                          <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span>UPLOAD IMAGE</span>
                        </Button>
                        <input
                          id="image-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </div>

            {imageResult && imageResult.faceDetected && (
              <div className="max-w-3xl mx-auto">
                <div className="mt-8 space-y-6 animate-fadeIn">
                  {/* Analyzed Image with Bounding Box */}
                  {imageResult.imageWithBox && (
                    <div className="relative border-2 border-cyan-200 rounded-xl p-4 bg-gradient-to-br from-white to-cyan-50 shadow-xl">
                      <div className="absolute -top-3 left-4 bg-gradient-to-r from-[#00BCD4] to-[#0097A7] px-4 py-1 rounded-full">
                        <span className="text-white text-sm font-medium">✓ Face Detected</span>
                      </div>
                      <img
                        src={`data:image/jpeg;base64,${imageResult.imageWithBox}`}
                        alt="Analyzed"
                        className="max-w-md mx-auto rounded-lg shadow-lg"
                      />
                    </div>
                  )}
                  
                  <div className="p-8 bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 rounded-xl border-2 border-cyan-200 shadow-xl">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#00BCD4] to-[#0097A7] rounded-full flex items-center justify-center">
                        <span className="text-white text-xl">📊</span>
                      </div>
                      <h4 className="text-2xl font-semibold text-gray-900">Analysis Results</h4>
                    </div>
                    <div className="space-y-6">
                      <div className="bg-white rounded-xl p-6 sm:p-10 shadow-md text-center">
                        <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider mb-4 sm:mb-6">Detected Emotion</p>
                        <p className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#00BCD4] to-[#0097A7] bg-clip-text text-transparent pb-2 break-words px-2">
                          {imageResult.detectedEmotion}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-3 rounded-lg border border-green-200">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium">Face successfully detected and analyzed</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-wrap items-center gap-4">
                    <Button
                      onClick={handleDownloadResult}
                      className="bg-gradient-to-r from-[#00BCD4] to-[#0097A7] hover:from-[#00ACC1] hover:to-[#00838F] text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Result
                    </Button>
                    <Button
                      onClick={handleCancelImage}
                      variant="outline"
                      className="border-gray-300 hover:bg-gray-50 transition-all hover:scale-105"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Analyze Another Image
                    </Button>
                  </div>
                </div>
              </div>
            )}
              
            {imageResult && !imageResult.faceDetected && (
              <div className="max-w-3xl mx-auto mt-8">
                <div className="mt-8 p-6 bg-gradient-to-br from-red-50 to-orange-50 rounded-lg border border-red-200">
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No Face Detected</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    {imageResult.error || "Please upload an image with a clear, visible face"}
                  </p>
                  <Button
                    onClick={handleCancelImage}
                    variant="outline"
                    className="border-red-300 hover:bg-red-50"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Try Another Image
                  </Button>
                </div>
              </div>
            )}

            <div className="max-w-3xl mx-auto mt-8">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-lg border border-amber-200">
                <h4 className="text-xl font-medium text-gray-900 mb-6">Tips for Best Results:</h4>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-0.5">•</span>
                    <span className="font-light">Ensure good lighting on the face</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-0.5">•</span>
                    <span className="font-light">Face should be clearly visible and not obscured</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-0.5">•</span>
                    <span className="font-light">Look directly at the camera for best accuracy</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-0.5">•</span>
                    <span className="font-light">Images work better with a single prominent face</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-amber-600 mt-0.5">•</span>
                    <span className="font-light">Supported formats: JPG, PNG, GIF, WebP</span>
                  </li>
                </ul>
              </div>
            </div>
          </TabsContent>

          {/* Advanced Tab */}
          <TabsContent value="advanced" className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-light text-gray-900 mb-3">
                Advanced Emotion Analysis Suite
              </h3>
              <p className="text-gray-600 font-light max-w-2xl mx-auto">
                Cutting-edge emotion detection for complex scenarios
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Multi-Person Detection */}
              <Card className="p-8 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900">Multi-Person Detection</h4>
                </div>
                
                <p className="text-sm text-gray-600 font-light mb-6">
                  Analyze emotions of multiple people simultaneously in group photos
                </p>

                {!multiPersonPreview ? (
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-purple-300 rounded-lg p-6 sm:p-8 text-center">
                      <Users className="w-12 h-12 text-purple-400 mx-auto mb-3" />
                      <p className="text-sm sm:text-base text-gray-700 font-light mb-1">Capture or Upload Group Photo</p>
                      <p className="text-xs text-gray-500">Analyze multiple people at once</p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
                      <Button
                        onClick={() => {
                          setCameraMode('multi');
                          setShowCamera(true);
                        }}
                        variant="outline"
                        className="flex items-center justify-center gap-2 px-4 sm:px-6 py-4 sm:py-6 text-sm sm:text-base border-2 border-purple-300 hover:border-purple-400 transition-colors w-full sm:w-auto"
                      >
                        <Camera className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span>START CAMERA</span>
                      </Button>
                      
                      <Button
                        variant="outline"
                        className="flex items-center justify-center gap-2 px-4 sm:px-6 py-4 sm:py-6 text-sm sm:text-base border-2 border-purple-300 hover:border-purple-400 transition-colors w-full sm:w-auto"
                        onClick={() => document.getElementById('multi-person-upload').click()}
                      >
                        <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span>UPLOAD IMAGE</span>
                      </Button>
                      <input
                        id="multi-person-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleMultiPersonUpload}
                        className="hidden"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="border-2 border-purple-300 rounded-lg p-2 bg-white">
                      <img
                        src={multiPersonPreview}
                        alt="Preview"
                        className="w-full rounded-lg"
                      />
                    </div>
                    
                    {!multiPersonResult && (
                      <div className="flex gap-3">
                        <Button
                          onClick={handleMultiPersonAnalysis}
                          disabled={isAnalyzingMultiPerson}
                          className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          {isAnalyzingMultiPerson ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Analyzing...
                            </>
                          ) : (
                            "ANALYZE GROUP"
                          )}
                        </Button>
                        <Button
                          onClick={() => {
                            setMultiPersonImage(null);
                            setMultiPersonPreview(null);
                            setMultiPersonResult(null);
                          }}
                          variant="outline"
                          className="border-purple-300"
                        >
                          Cancel
                        </Button>
                      </div>
                    )}

                    {multiPersonResult && (
                      <div className="space-y-4">
                        <div className="p-4 bg-white rounded-lg border border-purple-200">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium text-gray-700">
                              Detected {multiPersonResult.people_count} {multiPersonResult.people_count === 1 ? 'Person' : 'People'}
                            </p>
                            {multiPersonResult.grid_layout && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                                Grid: {multiPersonResult.grid_layout.rows} rows × {multiPersonResult.grid_layout.columns} columns
                              </span>
                            )}
                          </div>
                          
                          {/* Display emotions in a proper CSS Grid */}
                          {multiPersonResult.emotions && multiPersonResult.emotions.length > 0 && (
                            <div>
                              {(() => {
                                // Get grid layout info
                                const gridLayout = multiPersonResult.grid_layout;
                                const hasRowData = multiPersonResult.emotions.some(p => p.row !== undefined && p.column !== undefined);
                                
                                // Emotion color mapping
                                const emotionColors = {
                                  'Happy': 'bg-green-100 border-green-300 text-green-800',
                                  'Happiness': 'bg-green-100 border-green-300 text-green-800',
                                  'Joy': 'bg-green-100 border-green-300 text-green-800',
                                  'Excitement': 'bg-green-100 border-green-300 text-green-800',
                                  'Sad': 'bg-blue-100 border-blue-300 text-blue-800',
                                  'Sadness': 'bg-blue-100 border-blue-300 text-blue-800',
                                  'Sorrow': 'bg-blue-100 border-blue-300 text-blue-800',
                                  'Grief': 'bg-blue-100 border-blue-300 text-blue-800',
                                  'Angry': 'bg-red-100 border-red-300 text-red-800',
                                  'Anger': 'bg-red-100 border-red-300 text-red-800',
                                  'Rage': 'bg-red-100 border-red-300 text-red-800',
                                  'Frustrated': 'bg-orange-100 border-orange-300 text-orange-800',
                                  'Frustration': 'bg-orange-100 border-orange-300 text-orange-800',
                                  'Surprised': 'bg-yellow-100 border-yellow-300 text-yellow-800',
                                  'Surprise': 'bg-yellow-100 border-yellow-300 text-yellow-800',
                                  'Shock': 'bg-yellow-100 border-yellow-300 text-yellow-800',
                                  'Amazement': 'bg-yellow-100 border-yellow-300 text-yellow-800',
                                  'Fear': 'bg-purple-100 border-purple-300 text-purple-800',
                                  'Anxiety': 'bg-purple-100 border-purple-300 text-purple-800',
                                  'Worry': 'bg-purple-100 border-purple-300 text-purple-800',
                                  'Neutral': 'bg-gray-100 border-gray-300 text-gray-800',
                                  'Calm': 'bg-gray-100 border-gray-300 text-gray-800',
                                  'Peaceful': 'bg-gray-100 border-gray-300 text-gray-800',
                                  'Disgust': 'bg-lime-100 border-lime-300 text-lime-800',
                                  'Contempt': 'bg-lime-100 border-lime-300 text-lime-800',
                                  'Love': 'bg-pink-100 border-pink-300 text-pink-800',
                                  'Affection': 'bg-pink-100 border-pink-300 text-pink-800',
                                  'Pride': 'bg-indigo-100 border-indigo-300 text-indigo-800',
                                  'Confidence': 'bg-indigo-100 border-indigo-300 text-indigo-800',
                                  'Shame': 'bg-rose-100 border-rose-300 text-rose-800',
                                  'Embarrassment': 'bg-rose-100 border-rose-300 text-rose-800',
                                  'Confusion': 'bg-amber-100 border-amber-300 text-amber-800',
                                  'Bewilderment': 'bg-amber-100 border-amber-300 text-amber-800'
                                };
                                
                                if (hasRowData && gridLayout && gridLayout.columns) {
                                  // Use CSS Grid with proper positioning
                                  const numColumns = gridLayout.columns;
                                  
                                  return (
                                    <div 
                                      className="grid gap-2 w-full"
                                      style={{ 
                                        gridTemplateColumns: `repeat(${numColumns}, minmax(100px, 1fr))`,
                                        gridAutoRows: 'minmax(100px, auto)'
                                      }}
                                    >
                                      {multiPersonResult.emotions.map((person) => {
                                        const colorClass = emotionColors[person.emotion] || 'bg-purple-50 border-purple-200 text-purple-800';
                                        
                                        return (
                                          <div 
                                            key={person.person_id} 
                                            className={`p-3 ${colorClass} rounded-lg border-2 text-center flex flex-col items-center justify-center`}
                                            style={{
                                              gridRow: person.row,
                                              gridColumn: person.column
                                            }}
                                          >
                                            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center font-bold text-sm shadow-sm mb-2">
                                              {person.person_id}
                                            </div>
                                            <p className="text-sm font-bold truncate w-full px-1">{person.emotion}</p>
                                            {person.confidence && (
                                              <p className="text-xs opacity-75 mt-1">{person.confidence}%</p>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                }
                                
                                // Fallback: Simple grid if no row/column data
                                return (
                                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                                    {multiPersonResult.emotions.map((person) => {
                                      const colorClass = emotionColors[person.emotion] || 'bg-purple-50 border-purple-200 text-purple-800';
                                      
                                      return (
                                        <div 
                                          key={person.person_id} 
                                          className={`p-3 ${colorClass} rounded-lg border-2 text-center flex flex-col items-center justify-center min-h-[100px]`}
                                        >
                                          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center font-bold text-sm shadow-sm mb-2">
                                            {person.person_id}
                                          </div>
                                          <p className="text-sm font-bold truncate w-full px-1">{person.emotion}</p>
                                          {person.confidence && (
                                            <p className="text-xs opacity-75 mt-1">{person.confidence}%</p>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                          
                          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-xs text-blue-800">
                              <span className="font-semibold">💡 Tip:</span> People are numbered row by row, left to right. Each card shows the person number, emotion, and confidence level.
                            </p>
                          </div>
                        </div>

                        {/* Display analyzed image */}
                        <div className="border-2 border-purple-300 rounded-lg p-2 bg-white">
                          <img
                            src={multiPersonPreview}
                            alt="Analyzed Group"
                            className="w-full rounded-lg"
                          />
                        </div>

                        {/* Individual Person Results Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                          {multiPersonResult.emotions.map((person) => {
                            const emotionColors = {
                              'Happy': 'from-green-400 to-green-600',
                              'Happiness': 'from-green-400 to-green-600',
                              'Joy': 'from-green-400 to-green-600',
                              'Excitement': 'from-green-400 to-green-600',
                              'Sad': 'from-blue-400 to-blue-600',
                              'Sadness': 'from-blue-400 to-blue-600',
                              'Sorrow': 'from-blue-400 to-blue-600',
                              'Angry': 'from-red-400 to-red-600',
                              'Anger': 'from-red-400 to-red-600',
                              'Rage': 'from-red-400 to-red-600',
                              'Surprised': 'from-yellow-400 to-yellow-600',
                              'Surprise': 'from-yellow-400 to-yellow-600',
                              'Fear': 'from-purple-400 to-purple-600',
                              'Neutral': 'from-gray-400 to-gray-600'
                            };
                            const gradientClass = emotionColors[person.emotion] || 'from-purple-400 to-purple-600';
                            
                            return (
                              <div 
                                key={person.person_id} 
                                className="bg-white rounded-lg border-2 border-purple-200 p-4 hover:shadow-lg transition-shadow"
                              >
                                <div className="flex items-center gap-3 mb-3">
                                  <div className={`w-12 h-12 bg-gradient-to-br ${gradientClass} text-white rounded-full flex items-center justify-center text-lg font-bold shadow-md`}>
                                    {person.person_id}
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                                      {person.position || `Person ${person.person_id}`}
                                    </p>
                                    <p className="text-lg font-bold text-gray-900">
                                      {person.emotion}
                                    </p>
                                  </div>
                                </div>
                                
                                {person.confidence && (
                                  <div className="mt-2">
                                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                      <span>Confidence</span>
                                      <span className="font-semibold">{person.confidence}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                      <div 
                                        className={`h-full bg-gradient-to-r ${gradientClass} transition-all duration-500`}
                                        style={{ width: `${person.confidence}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        <Button
                          onClick={() => {
                            setMultiPersonImage(null);
                            setMultiPersonPreview(null);
                            setMultiPersonResult(null);
                          }}
                          variant="outline"
                          className="w-full border-purple-300"
                        >
                          Analyze Another Image
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Card>

              {/* Voice Emotion Analysis */}
              <Card className="p-8 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                    <Mic className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900">Voice Emotion Analysis</h4>
                </div>
                
                <p className="text-sm text-gray-600 font-light mb-6">
                  Speech-to-text emotion analysis using AI language models
                </p>

                <div className="space-y-6">
                  <div className="text-center py-8">
                    {!isRecording ? (
                      <Button
                        onClick={handleVoiceRecording}
                        className="w-32 h-32 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
                      >
                        <div className="flex flex-col items-center">
                          <Mic className="w-12 h-12" />
                          <span className="text-xs mt-2 font-semibold">START</span>
                        </div>
                      </Button>
                    ) : (
                      <Button
                        onClick={handleStopRecording}
                        className="w-32 h-32 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-lg animate-pulse"
                      >
                        <div className="flex flex-col items-center">
                          <div className="w-12 h-12 border-4 border-white rounded-full flex items-center justify-center">
                            <div className="w-6 h-6 bg-white rounded-sm"></div>
                          </div>
                          <span className="text-xs mt-2 font-semibold">STOP</span>
                        </div>
                      </Button>
                    )}
                  </div>

                  {voiceTranscript && (
                    <div className={`p-4 border rounded-lg ${isRecording ? 'bg-yellow-50 border-yellow-300' : 'bg-blue-50 border-blue-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <p className="text-sm font-medium text-gray-700">
                          {isRecording ? 'Listening...' : 'Transcript:'}
                        </p>
                        {isRecording && (
                          <div className="flex gap-1">
                            <div className="w-1 h-3 bg-yellow-500 rounded animate-pulse"></div>
                            <div className="w-1 h-3 bg-yellow-500 rounded animate-pulse" style={{animationDelay: '0.2s'}}></div>
                            <div className="w-1 h-3 bg-yellow-500 rounded animate-pulse" style={{animationDelay: '0.4s'}}></div>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 italic">"{voiceTranscript}"</p>
                    </div>
                  )}

                  {voiceResult && (
                    <div className="space-y-4">
                      {/* Main Emotion Result */}
                      <div className="p-6 bg-white rounded-lg border-2 border-blue-300 text-center">
                        <p className="text-sm text-gray-600 mb-2">Overall Emotion</p>
                        <p className="text-3xl font-bold text-blue-700">{voiceResult.emotion}</p>
                        {voiceResult.hybrid && (
                          <p className="text-xs text-amber-600 mt-2">⚠️ Words and tone show different emotions</p>
                        )}
                      </div>
                      
                      {/* Detailed Analysis */}
                      {(voiceResult.textEmotion || voiceResult.voiceEmotion) && (
                        <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                          <h5 className="text-sm font-semibold text-gray-700 mb-3">Detailed Analysis</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {voiceResult.textEmotion && (
                              <div className="p-3 bg-white rounded-lg border border-blue-200">
                                <div className="flex items-center gap-2 mb-2">
                                  <FileText className="w-4 h-4 text-blue-600" />
                                  <p className="text-xs font-semibold text-gray-600">From Words/Content</p>
                                </div>
                                <p className="text-lg font-bold text-gray-900">{voiceResult.textEmotion}</p>
                                <p className="text-xs text-gray-500 mt-1">Analyzed from speech content</p>
                              </div>
                            )}
                            {voiceResult.voiceEmotion && (
                              <div className="p-3 bg-white rounded-lg border border-indigo-200">
                                <div className="flex items-center gap-2 mb-2">
                                  <Mic className="w-4 h-4 text-indigo-600" />
                                  <p className="text-xs font-semibold text-gray-600">From Voice/Tone</p>
                                </div>
                                <p className="text-lg font-bold text-gray-900">{voiceResult.voiceEmotion}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Confidence: {voiceResult.voiceConfidence}% (pitch, tone, energy)
                                </p>
                              </div>
                            )}
                          </div>
                          
                          {/* Explanation */}
                          {voiceResult.hybrid && (
                            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-xs text-amber-800">
                                <strong>💡 Insight:</strong> Your words express "{voiceResult.textEmotion}" but your voice tone/pitch suggests "{voiceResult.voiceEmotion}". 
                                This is common when someone tries to hide their true feelings or expresses complex emotions.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>

              {/* Video Timeline Analysis */}
              <Card className="p-6 sm:p-8 bg-white border-gray-200 md:col-span-2">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gray-900 rounded-lg flex items-center justify-center">
                    <Video className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg sm:text-xl font-medium text-gray-900 tracking-tight">
                      Video Timeline Analysis
                    </h4>
                    <p className="text-xs text-gray-500 font-light">
                      Frame-by-frame emotion mapping across the entire timeline
                    </p>
                  </div>
                </div>

                {!videoPreview ? (
                  <label
                    htmlFor="video-upload"
                    data-testid="video-upload-zone"
                    className="mt-6 flex flex-col items-center justify-center py-12 px-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-gray-900 hover:bg-gray-50 transition-all cursor-pointer"
                  >
                    <Video className="w-10 h-10 text-gray-400 mb-3" strokeWidth={1.4} />
                    <p className="text-sm font-medium text-gray-900 tracking-tight">
                      Drop a video file or click to upload
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      MP4, MOV, WebM · analyzed every 2 seconds
                    </p>
                    <input
                      id="video-upload"
                      data-testid="video-upload-input"
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <>
                  <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-5">
                    {/* Video preview */}
                    <div className="lg:col-span-3">
                      <video
                        ref={videoPreviewRef}
                        data-testid="video-preview"
                        src={videoPreview}
                        controls
                        onTimeUpdate={(e) =>
                          setVideoCurrentTime(e.currentTarget.currentTime)
                        }
                        className="w-full rounded-xl bg-black aspect-video"
                      />
                      <div className="mt-3 flex items-center gap-3">
                        <button
                          data-testid="video-reset"
                          onClick={() => {
                            setVideoFile(null);
                            setVideoPreview(null);
                            setVideoTimeline([]);
                          }}
                          className="text-xs font-medium text-gray-700 hover:text-gray-900 px-3 py-1.5 border border-gray-300 rounded-md transition-colors"
                        >
                          Upload different video
                        </button>
                        {isAnalyzingVideo && (
                          <span className="inline-flex items-center gap-2 text-xs text-gray-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
                            Analyzing frames...
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Timeline panel */}
                    <div className="lg:col-span-2">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500">
                          Emotion timeline
                        </div>
                        <div className="text-[10px] font-mono text-gray-400">
                          {videoTimeline.length} frame{videoTimeline.length !== 1 ? "s" : ""}
                        </div>
                      </div>

                      {videoTimeline.length === 0 && !isAnalyzingVideo ? (
                        <div className="text-xs text-gray-500 font-light py-8 text-center bg-gray-50 rounded-lg">
                          Waiting for first frame...
                        </div>
                      ) : (
                        <div
                          className="max-h-[280px] overflow-y-auto pr-1 space-y-1.5"
                          data-testid="video-timeline-list"
                        >
                          {videoTimeline.map((frame, i) => {
                            const minutes = Math.floor(frame.time / 60);
                            const seconds = Math.floor(frame.time % 60);
                            const timeLabel = `${minutes}:${seconds
                              .toString()
                              .padStart(2, "0")}`;
                            const emotionColors = {
                              happy: "#FFB74D",
                              sad: "#64B5F6",
                              angry: "#FF6B6B",
                              fear: "#A78BFA",
                              surprise: "#4DD0E1",
                              disgust: "#81C784",
                              neutral: "#9E9E9E",
                            };
                            const color =
                              emotionColors[frame.emotion?.toLowerCase()] || "#666";
                            return (
                              <div
                                key={i}
                                data-testid={`timeline-frame-${i}`}
                                className="group flex items-center gap-3 px-3 py-2 bg-white border border-gray-100 rounded-lg hover:border-gray-300 transition-colors"
                              >
                                <span className="text-[10px] font-mono text-gray-400 w-10">
                                  {timeLabel}
                                </span>
                                <span
                                  className="w-2 h-2 rounded-full flex-shrink-0"
                                  style={{ background: color }}
                                />
                                <span className="text-xs font-medium text-gray-900 capitalize flex-1">
                                  {frame.emotion}
                                </span>
                                <span
                                  className="text-[10px] font-mono"
                                  style={{ color }}
                                >
                                  {Math.round(frame.confidence)}%
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Quick distribution chip */}
                      {videoTimeline.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-dashed border-gray-200">
                          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 mb-2">
                            Quick view
                          </div>
                          {(() => {
                            const counts = {};
                            videoTimeline.forEach((f) => {
                              const e = f.emotion?.toLowerCase() || "unknown";
                              counts[e] = (counts[e] || 0) + 1;
                            });
                            const total = videoTimeline.length;
                            const emotionColors = {
                              happy: "#FFB74D",
                              sad: "#64B5F6",
                              angry: "#FF6B6B",
                              fear: "#A78BFA",
                              surprise: "#4DD0E1",
                              disgust: "#81C784",
                              neutral: "#9E9E9E",
                            };
                            return (
                              <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
                                {Object.entries(counts).map(([emo, c]) => (
                                  <div
                                    key={emo}
                                    title={`${emo}: ${c}`}
                                    style={{
                                      width: `${(c / total) * 100}%`,
                                      background: emotionColors[emo] || "#666",
                                    }}
                                  />
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Full-width Emotion Arc Chart (line + ribbon) */}
                  {videoTimeline.length > 1 && (
                    <EmotionArc
                      timeline={videoTimeline}
                      currentTime={videoCurrentTime}
                      onSeek={(time) => {
                        const v = videoPreviewRef.current;
                        if (v) {
                          v.currentTime = time;
                          v.play().catch(() => {});
                        }
                      }}
                    />
                  )}
                  </>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6" key={`dashboard-${analytics.totalAnalyses}-${analytics.lastAnalysisTime}`}>
            <div className="text-center mb-8">
              <h3 className="text-3xl font-light text-gray-900 mb-3">
                Live Research Dashboard
              </h3>
              <p className="text-gray-600 font-light max-w-2xl mx-auto">
                Real-time analytics and insights from our emotion analysis platform
              </p>
              <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-green-100 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-green-700">Live Data</span>
              </div>
            </div>

            {/* Top Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 text-center">
                <p className="text-xs text-gray-600 mb-2">Total Analyses</p>
                <p className="text-3xl font-bold text-gray-900">{analytics.totalAnalyses || 0}</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200 text-center">
                <p className="text-xs text-gray-600 mb-2">Text Analysis</p>
                <p className="text-3xl font-bold text-gray-900">{analytics.featureUsage.text}</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 text-center">
                <p className="text-xs text-gray-600 mb-2">Image Analysis</p>
                <p className="text-3xl font-bold text-gray-900">{analytics.featureUsage.image}</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 text-center">
                <p className="text-xs text-gray-600 mb-2">Multi-Person</p>
                <p className="text-3xl font-bold text-gray-900">{analytics.featureUsage.multiPerson}</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200 text-center">
                <p className="text-xs text-gray-600 mb-2">Voice Analysis</p>
                <p className="text-3xl font-bold text-gray-900">{analytics.featureUsage.voice}</p>
                <p className="text-xs text-gray-500 mt-1">analyses</p>
              </Card>
            </div>

            {/* Charts Section */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* 24-Hour Activity Chart */}
              <Card className="p-6 bg-white border-gray-200">
                <div className="flex items-center gap-2 mb-6">
                  <BarChart3 className="w-5 h-5 text-gray-700" />
                  <h4 className="text-lg font-semibold text-gray-900">24-Hour Activity</h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">Analyses per Hour</p>
                
                {analytics.totalAnalyses > 0 ? (
                  <div className="space-y-3">
                    <div className="flex items-end justify-between h-32 gap-1">
                      {analytics.hourlyData.map((count, hour) => {
                        const maxCount = Math.max(...analytics.hourlyData, 1);
                        const heightPercent = (count / maxCount) * 100;
                        const displayHour = hour % 12 || 12;
                        const period = hour < 12 ? 'AM' : 'PM';
                        const showLabel = hour % 4 === 0;
                        
                        return (
                          <div key={hour} className="flex-1 flex flex-col items-center gap-1 group relative">
                            <div 
                              className="w-full bg-gradient-to-t from-cyan-400 to-cyan-300 rounded-t transition-all hover:from-cyan-500 hover:to-cyan-400" 
                              style={{ height: count > 0 ? `${Math.max(heightPercent, 10)}%` : '3%' }}
                              title={`${displayHour}:00 ${period} - ${count} analyses`}
                            ></div>
                            {showLabel && (
                              <span className="text-[10px] text-gray-500">{displayHour}{period}</span>
                            )}
                            {/* Tooltip */}
                            <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                              {displayHour}:00 {period}: {count}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="flex items-center justify-center gap-4 pt-4 text-xs text-gray-600">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-cyan-400 rounded"></div>
                        <span>Hourly Analyses</span>
                      </div>
                      <span className="text-gray-400">•</span>
                      <span>Total: {analytics.totalAnalyses}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                    <BarChart3 className="w-12 h-12 mb-2 opacity-30" />
                    <p className="text-sm">No activity yet</p>
                    <p className="text-xs mt-1">Start analyzing emotions to see hourly activity</p>
                  </div>
                )}
              </Card>

              {/* Overall Emotion Distribution */}
              <Card className="p-6 bg-white border-gray-200">
                <div className="flex items-center gap-2 mb-6">
                  <Globe className="w-5 h-5 text-gray-700" />
                  <h4 className="text-lg font-semibold text-gray-900">Overall Emotion Distribution</h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">Emotion Breakdown</p>
                
                <div className="space-y-3">
                  {getEmotionDistribution().map((item) => (
                    <div key={item.emotion} className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                      <span className="text-sm text-gray-700 w-20">{item.emotion}</span>
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${item.color}`} style={{ width: `${item.percent}%` }}></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-12 text-right">{item.percent}%</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            {/* Feature Usage Statistics */}
            <Card className="p-6 bg-white border-gray-200">
              <div className="flex items-center gap-2 mb-6">
                <Activity className="w-5 h-5 text-gray-700" />
                <h4 className="text-lg font-semibold text-gray-900">Feature Usage Statistics</h4>
              </div>
              <p className="text-sm text-gray-600 mb-6">Platform Features</p>
              
              <div className="space-y-4">
                {[
                  { feature: 'Text Analysis', value: analytics.featureUsage.text, color: 'bg-blue-500' },
                  { feature: 'Image Analysis', value: analytics.featureUsage.image, color: 'bg-teal-500' },
                  { feature: 'Multi-Person', value: analytics.featureUsage.multiPerson, color: 'bg-purple-500' },
                  { feature: 'Voice Analysis', value: analytics.featureUsage.voice, color: 'bg-orange-500' },
                  { feature: '3D Visualization', value: analytics.featureUsage.visualization3D, color: 'bg-pink-500' }
                ].map((item) => {
                  const maxValue = Math.max(
                    analytics.featureUsage.text,
                    analytics.featureUsage.image,
                    analytics.featureUsage.multiPerson,
                    analytics.featureUsage.voice,
                    analytics.featureUsage.visualization3D
                  ) || 1;
                  return (
                    <div key={item.feature}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-700">{item.feature}</span>
                        <span className="text-sm font-medium text-gray-900">{item.value}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${item.color}`} style={{ width: `${(item.value / maxValue) * 100}%` }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Bottom Info Cards */}
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 text-center">
                <div className="text-4xl mb-3">😊</div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Top Detected Emotion</h4>
                <p className="text-2xl font-bold text-blue-700 mb-1">
                  {getTopEmotion().emotion.charAt(0).toUpperCase() + getTopEmotion().emotion.slice(1)} 
                  {getTopEmotion().percentage > 0 ? ` (${getTopEmotion().percentage}%)` : ''}
                </p>
                <p className="text-xs text-gray-600">Most common emotion detected</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-green-50 to-teal-50 border-green-200 text-center">
                <div className="text-4xl mb-3">⏰</div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Peak Usage Time</h4>
                <p className="text-2xl font-bold text-green-700 mb-1">{getPeakHour()}</p>
                <p className="text-xs text-gray-600">Highest activity period</p>
              </Card>

              <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200 text-center">
                <div className="text-4xl mb-3">📊</div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Session Analyses</h4>
                <p className="text-2xl font-bold text-purple-700 mb-1">{analytics.totalAnalyses}</p>
                <p className="text-xs text-gray-600">Total analyses this session</p>
              </Card>
            </div>

            {/* Live Analysis Feed */}
            <Card className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-gray-700 animate-pulse" />
                  <h4 className="text-lg font-semibold text-gray-900">Live Analysis Feed</h4>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-gray-600">Real-time updates</span>
                </div>
              </div>
              
              <div className="space-y-3 max-h-48 overflow-y-auto">
                {analytics.recentAnalyses.length > 0 ? (
                  analytics.recentAnalyses.map((item) => {
                    const typeIcons = {
                      text: '📝',
                      image: '🖼️',
                      multiPerson: '👥',
                      voice: '🎤'
                    };
                    const typeLabels = {
                      text: 'Text analysis',
                      image: 'Image analysis',
                      multiPerson: 'Multi-person analysis',
                      voice: 'Voice analysis'
                    };
                    return (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100 hover:border-amber-300 transition-colors">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{typeIcons[item.type] || '📊'}</span>
                          <span className="text-sm text-gray-700">
                            {typeLabels[item.type]} - {item.emotion} detected ({item.confidence}%)
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">{item.timeAgo}</span>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">No analyses yet</p>
                    <p className="text-xs mt-2">Start analyzing to see real-time updates here</p>
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* AI Chat Tab */}
          <TabsContent value="chat" className="space-y-8 px-4">
            <div className="text-center mb-8">
              <h3 className="text-2xl sm:text-3xl font-light text-gray-900 mb-3">
                AI Research Assistant
              </h3>
              <p className="text-sm sm:text-base text-gray-600 font-light max-w-2xl mx-auto px-4">
                Ask questions about our emotion AI research and technology
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <Card className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
                <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                  {chatMessages.map((msg, index) => (
                    <div key={index} className="flex items-start gap-3">
                      {msg.role === 'assistant' ? (
                        <>
                          <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1 bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-sm text-gray-700">{msg.content}</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex-1"></div>
                          <div className="bg-indigo-500 text-white p-4 rounded-lg shadow-sm max-w-md">
                            <p className="text-sm">{msg.content}</p>
                          </div>
                          <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                            <Users className="w-4 h-4 text-gray-600" />
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 sm:gap-3">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && chatInput.trim()) {
                        handleChatQuestion(chatInput);
                        setChatInput('');
                      }
                    }}
                    placeholder="Ask about our research..."
                    className="flex-1 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-0"
                  />
                  <Button 
                    onClick={() => {
                      if (chatInput.trim()) {
                        handleChatQuestion(chatInput);
                        setChatInput('');
                      }
                    }}
                    className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white px-4 sm:px-6 flex-shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  <Button 
                    onClick={() => handleChatQuestion("How accurate is your model?")}
                    variant="outline" 
                    size="sm" 
                    className="text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  >
                    How accurate is your model?
                  </Button>
                  <Button 
                    onClick={() => handleChatQuestion("What datasets do you use?")}
                    variant="outline" 
                    size="sm" 
                    className="text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  >
                    What datasets do you use?
                  </Button>
                  <Button 
                    onClick={() => handleChatQuestion("Explain FER2013 model")}
                    variant="outline" 
                    size="sm" 
                    className="text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  >
                    Explain FER2013 model
                  </Button>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Lab Tour Tab */}
          <TabsContent value="lab" className="space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-light text-gray-900 mb-3">
                Virtual Lab Tour
              </h3>
              <p className="text-gray-600 font-light max-w-2xl mx-auto mb-4">
                Explore our advanced emotion AI research facilities in interactive 360° • Drag to look around, scroll to zoom
              </p>
              <div className="flex justify-center gap-2">
                <div className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md bg-purple-100 text-purple-800 border border-transparent">
                  <Eye className="w-3 h-3 mr-1" />
                  Interactive Experience
                </div>
                <div className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md bg-green-100 text-green-800 border border-transparent">
                  <Zap className="w-3 h-3 mr-1" />
                  Real Equipment
                </div>
              </div>
            </div>

            {/* Navigation Controls */}
            <div className="flex justify-center mb-6">
              <div className="flex gap-2">
                <Button 
                  onClick={() => setCurrentStation(Math.max(0, currentStation - 1))}
                  disabled={currentStation === 0 || isAutoTour}
                  variant="outline"
                  size="sm"
                  className="bg-white"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button 
                  onClick={() => setIsAutoTour(!isAutoTour)}
                  size="sm"
                  className={`${isAutoTour ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-900 hover:bg-black'} text-white transition-colors`}
                >
                  {isAutoTour ? (
                    <>
                      <Square className="w-4 h-4 mr-1" />
                      Stop Tour
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-1" />
                      Auto Tour
                    </>
                  )}
                </Button>
                <Button 
                  onClick={() => setCurrentStation(Math.min(labStations.length - 1, currentStation + 1))}
                  disabled={currentStation === labStations.length - 1 || isAutoTour}
                  variant="outline"
                  size="sm"
                  className="bg-white"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Dot Navigation */}
            <div className="flex justify-center mb-8">
              <div className="flex gap-2">
                {labStations.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => !isAutoTour && setCurrentStation(index)}
                    disabled={isAutoTour}
                    className={`w-3 h-3 rounded-full transition-all ${
                      currentStation === index 
                        ? 'bg-blue-500 scale-125' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    } ${isAutoTour ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                    aria-label={`Go to station ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Station Content */}
            <div className="max-w-5xl mx-auto">
              <Card className="p-6 bg-white border-none shadow-lg rounded-xl">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xl font-semibold text-gray-900">
                    {labStations[currentStation].title}
                  </h4>
                  <div className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-md border border-gray-300 text-gray-700">
                    Station {currentStation + 1}/4
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Image Section - 360° Interactive Viewer */}
                  <div className="relative group">
                    {labStations[currentStation].image && labStations[currentStation].is360 ? (
                      <div className="w-full h-64 sm:h-80 lg:h-96 rounded-lg overflow-hidden relative">
                        <PanoramaViewer 
                          key={`panorama-${currentStation}`}
                          image={labStations[currentStation].image}
                          title={labStations[currentStation].title}
                        />
                        <div className="absolute top-2 left-2 bg-blue-600/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-medium z-[100] flex items-center gap-1.5 pointer-events-none">
                          <RotateCw className="w-3 h-3" />
                          360° Interactive View
                        </div>
                        <div className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-1.5 rounded-full text-xs font-medium z-[100] pointer-events-none">
                          🖱️ Drag to explore • 🔍 Scroll to zoom
                        </div>
                      </div>
                    ) : labStations[currentStation].image ? (
                      <div className="w-full h-64 sm:h-80 lg:h-96 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg overflow-hidden relative">
                        <img 
                          src={labStations[currentStation].image}
                          alt={labStations[currentStation].title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-2 left-2 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-medium z-10">
                          Lab Facility View
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-64 sm:h-80 lg:h-96 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                        <div className="text-white text-center">
                          <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p className="text-lg font-semibold">Lab Facility</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Equipment Details */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      {labStations[currentStation].title}
                    </h3>
                    <p className="text-gray-600 font-light mb-4">
                      {labStations[currentStation].description}
                    </p>
                    <div className="space-y-2">
                      <h4 className="font-semibold mb-2 text-gray-900">Research Equipment:</h4>
                      {labStations[currentStation].equipment.map((item, idx) => {
                        const IconComponent = item.icon;
                        return (
                          <div 
                            key={idx}
                            className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <IconComponent className={`w-5 h-5 text-${item.color}-500`} />
                              <div className="flex-1">
                                <h5 className="font-medium text-gray-900">{item.name}</h5>
                                <p className="text-sm text-gray-600 font-light">{item.desc}</p>
                              </div>
                              <Info className="w-4 h-4 text-gray-400" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
              <Card className="p-4 text-center bg-white border-none shadow-md rounded-xl">
                <Database className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <h4 className="font-semibold text-gray-900 mb-1">100TB+</h4>
                <p className="text-sm text-gray-600 font-light">Research Data</p>
              </Card>
              <Card className="p-4 text-center bg-white border-none shadow-md rounded-xl">
                <Cpu className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                <h4 className="font-semibold text-gray-900 mb-1">624 TOPS</h4>
                <p className="text-sm text-gray-600 font-light">AI Performance</p>
              </Card>
              <Card className="p-4 text-center bg-white border-none shadow-md rounded-xl">
                <Camera className="w-8 h-8 mx-auto mb-2 text-orange-500" />
                <h4 className="font-semibold text-gray-900 mb-1">4K 60fps</h4>
                <p className="text-sm text-gray-600 font-light">Video Capture</p>
              </Card>
              <Card className="p-4 text-center bg-white border-none shadow-md rounded-xl">
                <Brain className="w-8 h-8 mx-auto mb-2 text-pink-500" />
                <h4 className="font-semibold text-gray-900 mb-1">256 Channels</h4>
                <p className="text-sm text-gray-600 font-light">Neural Recording</p>
              </Card>
            </div>

            {/* Progress Indicator */}
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600 font-light mb-2">
                Station {currentStation + 1} of 4 • {isAutoTour ? '🎬 Auto Tour Active' : 'Manual Navigation'}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2 max-w-md mx-auto">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${((currentStation + 1) / labStations.length) * 100}%` }}
                />
              </div>
            </div>
          </TabsContent>

          {/* Sandbox Tab */}
          <TabsContent value="sandbox" className="space-y-6 px-4">
            <div className="text-center mb-6">
              <h3 className="text-2xl sm:text-3xl font-light text-gray-900 mb-3">
                Collaborative Research Sandbox
              </h3>
              <p className="text-sm sm:text-base text-gray-600 font-light max-w-2xl mx-auto mb-4 px-4">
                Real-time collaboration platform for emotion analysis research and sharing
              </p>
              
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-6 px-4">
                <div className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-green-100 rounded-full">
                  <Users className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                  <span className="text-xs sm:text-sm font-medium text-green-700">2 Online</span>
                </div>
                <div className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-100 rounded-full">
                  <Globe className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" />
                  <span className="text-xs sm:text-sm font-medium text-blue-700">Global Network</span>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Share Your Analysis */}
              <div className="md:col-span-2 space-y-6">
                <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Globe className="w-5 h-5 text-gray-700" />
                    <h4 className="text-lg font-semibold text-gray-900">Share Your Analysis</h4>
                  </div>
                  
                  <Textarea
                    value={sandboxText}
                    onChange={(e) => setSandboxText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey && sandboxText.trim()) {
                        handleSandboxShare();
                      }
                    }}
                    placeholder="Enter text for emotion analysis to share with collaborators... (Ctrl+Enter to send)"
                    className="min-h-[120px] mb-3 bg-white"
                    maxLength={1000}
                  />
                  
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
                    <Button 
                      onClick={handleSandboxShare}
                      disabled={!sandboxText.trim() || sandboxAnalyzing}
                      className="bg-gray-700 hover:bg-gray-800 text-white disabled:opacity-50 w-full sm:w-auto text-sm sm:text-base"
                    >
                      {sandboxAnalyzing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          <span className="hidden sm:inline">Analyzing...</span>
                          <span className="sm:hidden">Analyzing...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          <span className="hidden sm:inline">ANALYZE & SHARE</span>
                          <span className="sm:hidden">ANALYZE</span>
                        </>
                      )}
                    </Button>
                    <span className="text-xs text-gray-500 text-center sm:text-left">{sandboxText.length}/1000</span>
                  </div>
                </Card>

                {/* Shared Analysis Feed */}
                <Card className="p-6 bg-white border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-gray-700" />
                      <h4 className="text-lg font-semibold text-gray-900">Shared Analysis Feed</h4>
                    </div>
                    <span className="text-sm text-gray-500">{sharedAnalyses.length} Shared</span>
                  </div>

                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {sharedAnalyses.length > 0 ? (
                      sharedAnalyses.map((analysis) => {
                        const emotionColors = {
                          'Joy': 'bg-green-500',
                          'Sadness': 'bg-blue-500',
                          'Anger': 'bg-red-500',
                          'Fear': 'bg-purple-500',
                          'Surprise': 'bg-yellow-500',
                          'Disgust': 'bg-orange-500',
                          'Neutral': 'bg-gray-400'
                        };
                        const timeAgo = Math.floor((Date.now() - analysis.timestamp.getTime()) / 1000);
                        const timeStr = timeAgo < 60 ? 'Just now' : 
                                       timeAgo < 3600 ? `${Math.floor(timeAgo / 60)}m ago` : 
                                       `${Math.floor(timeAgo / 3600)}h ago`;
                        
                        return (
                          <div key={analysis.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 bg-gradient-to-br ${analysis.user === 'You' ? 'from-cyan-400 to-cyan-600' : 'from-blue-400 to-blue-600'} rounded-full flex items-center justify-center text-white font-bold text-xs`}>
                                  {analysis.avatar}
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900 text-sm">{analysis.user}</p>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span>Shared Analysis</span>
                                    <span>•</span>
                                    <span>{timeStr}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <p className="text-sm text-gray-700 mb-3 italic">
                              "{analysis.text}"
                            </p>
                            
                            <div className="flex items-center gap-3 text-xs mb-3">
                              <div className="flex items-center gap-1">
                                <div className={`w-2 h-2 ${emotionColors[analysis.emotion]} rounded-full`}></div>
                                <span className="text-gray-600 font-medium">Detected: {analysis.emotion}</span>
                              </div>
                            </div>

                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <div className="flex items-center gap-3">
                                <button className="flex items-center gap-1 hover:text-red-500 transition-colors">
                                  <span>❤️</span> {analysis.likes}
                                </button>
                                <button className="flex items-center gap-1 hover:text-blue-500 transition-colors">
                                  <MessageSquare className="w-3 h-3" /> {analysis.comments}
                                </button>
                              </div>
                              <button className="text-blue-600 hover:underline flex items-center gap-1">
                                <Globe className="w-3 h-3" />
                                View Details
                              </button>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-12 text-gray-400">
                        <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm font-medium">No shared analyses yet</p>
                        <p className="text-xs mt-2">Share your emotion analysis to collaborate with others</p>
                      </div>
                    )}
                  </div>
                </Card>
              </div>

              {/* Right Sidebar */}
              <div className="space-y-6">
                {/* Active Collaborators */}
                <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-5 h-5 text-gray-700" />
                    <h4 className="text-lg font-semibold text-gray-900">Active Collaborators</h4>
                  </div>

                  <div className="space-y-3">
                    {(() => {
                      // Get unique users from shared analyses and chat
                      const uniqueUsers = new Set();
                      sharedAnalyses.forEach(a => uniqueUsers.add(a.user));
                      collabChatMessages.forEach(m => uniqueUsers.add(m.user));
                      const activeUsers = Array.from(uniqueUsers).slice(0, 5);
                      
                      if (activeUsers.length === 0) {
                        return (
                          <div className="text-center py-6 text-gray-400">
                            <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">No active collaborators</p>
                            <p className="text-xs mt-1">Be the first to share an analysis!</p>
                          </div>
                        );
                      }
                      
                      return activeUsers.map((user, index) => {
                        const colors = ['pink', 'orange', 'teal', 'blue', 'purple', 'indigo'];
                        const color = colors[index % colors.length];
                        const isYou = user === 'You';
                        
                        return (
                          <div key={user} className="flex items-center justify-between p-3 bg-white rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 bg-gradient-to-br from-${color}-400 to-${color}-600 rounded-full flex items-center justify-center text-white font-bold text-xs`}>
                                {isYou ? 'ME' : user.substring(0, 2).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900 text-sm">{user}</p>
                                <div className="flex items-center gap-1">
                                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                  <span className="text-xs text-gray-600">Active</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>

                  <div className="mt-4 p-3 bg-white/50 rounded-lg text-center">
                    <p className="text-xs text-gray-600">
                      <strong>{new Set([...sharedAnalyses.map(a => a.user), ...collabChatMessages.map(m => m.user)]).size}</strong> researchers active
                    </p>
                  </div>
                </Card>

                {/* Research Chat */}
                <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                  <div className="flex items-center gap-2 mb-4">
                    <MessageSquare className="w-5 h-5 text-gray-700" />
                    <h4 className="text-lg font-semibold text-gray-900">Research Chat</h4>
                    <span className="ml-auto text-xs text-gray-500">{collabChatMessages.length} messages</span>
                  </div>

                  <div className="space-y-3 mb-4 max-h-64 overflow-y-auto bg-white rounded-lg p-3 border border-purple-100">
                    {collabChatMessages.map((msg) => {
                      const timeStr = msg.timestamp.toLocaleTimeString('en-US', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      });
                      const isYou = msg.user === 'You';
                      
                      return (
                        <div key={msg.id} className={`flex items-start gap-2 ${isYou ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-7 h-7 bg-gradient-to-br ${isYou ? 'from-cyan-400 to-cyan-600' : 'from-pink-400 to-pink-600'} rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                            {msg.avatar}
                          </div>
                          <div className={`flex-1 ${isYou ? 'text-right' : ''}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <p className={`font-semibold text-gray-900 text-xs ${isYou ? 'ml-auto' : ''}`}>
                                {msg.user}
                              </p>
                              <p className="text-gray-500 text-xs">{timeStr}</p>
                            </div>
                            <p className={`text-gray-700 text-xs p-2 rounded-lg inline-block ${isYou ? 'bg-cyan-100' : 'bg-gray-100'}`}>
                              {msg.message}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={collabChatMessage}
                      onChange={(e) => setCollabChatMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && collabChatMessage.trim()) {
                          handleSendChatMessage();
                        }
                      }}
                      placeholder="Share insights..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white min-w-0"
                    />
                    <Button 
                      size="sm" 
                      onClick={handleSendChatMessage}
                      disabled={!collabChatMessage.trim()}
                      className="bg-purple-500 hover:bg-purple-600 text-white disabled:opacity-50 flex-shrink-0"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
        
        {/* Camera Capture Modal */}
        {showCamera && (
          <CameraCapture
            onCapture={handleCameraCapture}
            onClose={() => setShowCamera(false)}
          />
        )}
      </div>
    </section>
  );
};

export default DemoSection;